import { useEffect, useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getStatusesByCategory, StatusConfig } from '@/services/status.service';
import { getColorClasses, normalizeHexColor, hexToRgb } from '@/utils/statusUtils';

export interface StatusOption {
  value: string; // Status name (used for API updates)
  label: string; // Display name
  color: string; // Tailwind classes
  id?: string; // Status ID from API
  code?: string; // Status code (e.g., AWAITING_FEEDBACK)
  hexColor?: string; // Original hex color from API for inline styles
}

interface StatusDropdownProps {
  currentStatus: string; // Can be status name, ID, or code
  onStatusChange: (statusId: string, statusName: string) => void; // Now passes both ID and name
  disabled?: boolean;
  className?: string;
}

// Fallback status options (for backward compatibility)
// These use names as values to match what the API expects, but also include codes for matching
const FALLBACK_STATUS_OPTIONS: StatusOption[] = [
  { value: 'Not Started', label: 'Not Started', code: 'NOT_STARTED', color: 'bg-gray-100 text-gray-800 border-gray-200' },
  { value: 'In Progress', label: 'In Progress', code: 'IN_PROGRESS', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  { value: 'Testing', label: 'Testing', code: 'TESTING', color: 'bg-purple-100 text-purple-800 border-purple-200' },
  { value: 'Awaiting Feedback', label: 'Awaiting Feedback', code: 'AWAITING_FEEDBACK', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  { value: 'Ready for Production', label: 'Ready for Production', code: 'READY_FOR_PRODUCTION', color: 'bg-green-100 text-green-800 border-green-200' },
  { value: 'On Hold', label: 'On Hold', code: 'ON_HOLD', color: 'bg-red-100 text-red-800 border-red-200' },
  { value: 'Complete', label: 'Complete', code: 'COMPLETE', color: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
];

export function StatusDropdown({ 
  currentStatus, 
  onStatusChange, 
  disabled = false,
  className 
}: StatusDropdownProps) {
  const [statusOptions, setStatusOptions] = useState<StatusOption[]>(FALLBACK_STATUS_OPTIONS);
  const [taskStatuses, setTaskStatuses] = useState<StatusConfig[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTaskStatuses = async () => {
      try {
        setLoading(true);
        const statuses = await getStatusesByCategory('task');
        setTaskStatuses(statuses);
        
        // Convert API statuses to StatusOption format
        const options: StatusOption[] = statuses
          .filter(status => status.isActive) // Only show active statuses
          .map(status => {
            // Generate common status codes for matching
            const code = status.name.toUpperCase().replace(/\s+/g, '_');
            // Try to get Tailwind classes, but also store the hex color for inline styles
            const tailwindColor = getColorClasses(status.color);
            return {
              value: status.name, // Use status name as value
              label: status.name,
              color: tailwindColor,
              id: status.id,
              code: code,
              hexColor: status.color, // Store original hex color for inline styles
            };
          })
          .sort((a, b) => a.label.localeCompare(b.label)); // Sort alphabetically
        
        if (options.length > 0) {
          setStatusOptions(options);
        }
      } catch (error) {
        console.error('Error fetching task statuses:', error);
        // Keep fallback options on error
      } finally {
        setLoading(false);
      }
    };

    fetchTaskStatuses();
  }, []);

  // Match status by multiple criteria:
  // 1. Exact match with value (name)
  // 2. Exact match with label (name)
  // 3. Match with status ID
  // 4. Match with status code (e.g., AWAITING_FEEDBACK)
  // 5. Case-insensitive match
  const currentStatusOption = statusOptions.find(option => {
    // Ensure currentStatus is a string
    if (!currentStatus) return false;
    const currentStatusStr = String(currentStatus).trim();
    if (!currentStatusStr) return false;
    
    const currentUpper = currentStatusStr.toUpperCase();
    const optionValueUpper = String(option.value || '').toUpperCase().trim();
    const optionLabelUpper = String(option.label || '').toUpperCase().trim();
    const optionCodeUpper = String(option.code || '').toUpperCase().trim();
    
    const matches = (
      option.value === currentStatusStr ||
      option.label === currentStatusStr ||
      option.id === currentStatusStr ||
      option.code === currentStatusStr ||
      optionValueUpper === currentUpper ||
      optionLabelUpper === currentUpper ||
      optionCodeUpper === currentUpper ||
      currentUpper === optionCodeUpper.replace(/_/g, ' ')
    );
    
    return matches;
  }) || (statusOptions.length > 0 ? statusOptions[0] : null);
  
  // Log for debugging if no match found
  if (currentStatus && !currentStatusOption && statusOptions.length > 0) {
    console.warn('[StatusDropdown] No match found for status:', currentStatus, 'Available options:', statusOptions.map(o => ({ value: o.value, code: o.code })));
  }

  const handleStatusChange = (selectedOption: StatusOption) => {
    if (selectedOption.value !== currentStatus) {
      // Pass the status ID (required by API) and name (for display)
      // If no ID available (fallback), use the value as ID (though API will reject it)
      // This should only happen with fallback statuses
      const statusId = selectedOption.id || selectedOption.value;
      onStatusChange(statusId, selectedOption.value);
    }
  };

  if (loading) {
    return (
      <button
        className={cn(
          'flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border opacity-50 cursor-not-allowed',
          className
        )}
        disabled
      >
        <span>Loading...</span>
      </button>
    );
  }

  // If no status option found, show the current status as-is or a default
  const currentStatusStr = currentStatus ? String(currentStatus) : '';
  const displayOption = currentStatusOption || {
    value: currentStatusStr || 'Unknown',
    label: currentStatusStr || 'Unknown',
    color: 'bg-gray-100 text-gray-800 border-gray-200',
    hexColor: '#6B7280',
  };

  // Always use inline styles when we have a hex color from API to ensure exact colors are displayed
  const useInlineStyle = !!displayOption.hexColor;
  const inlineStyle = useInlineStyle && displayOption.hexColor ? (() => {
    const rgb = hexToRgb(displayOption.hexColor);
    if (rgb) {
      return {
        backgroundColor: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.1)`, // 10% opacity for background
        color: normalizeHexColor(displayOption.hexColor),
        borderColor: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.3)`, // 30% opacity for border
      };
    }
    return {
      backgroundColor: '#F3F4F6',
      color: normalizeHexColor(displayOption.hexColor),
      borderColor: '#E5E7EB',
    };
  })() : {};

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            'flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border transition-colors hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500',
            // Use Tailwind classes as fallback if no hex color available
            !useInlineStyle && displayOption.color,
            disabled && 'opacity-50 cursor-not-allowed',
            className
          )}
          style={useInlineStyle ? inlineStyle : undefined}
          disabled={disabled}
        >
          <span>{displayOption.label}</span>
          <ChevronDown className="h-3 w-3" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-48">
        {statusOptions.map((option) => (
          <DropdownMenuItem
            key={option.value}
            onClick={() => handleStatusChange(option)}
            className={cn(
              'flex items-center gap-2 cursor-pointer',
              (option.value === currentStatus || option.label === currentStatus || option.id === currentStatus) && 'bg-accent'
            )}
          >
            <div 
              className="w-2 h-2 rounded-full border border-gray-200" 
              style={{ backgroundColor: taskStatuses.find(s => s.name === option.value)?.color || '#6B7280' }}
            />
            <span>Mark as {option.label}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default StatusDropdown;
